import { AfterViewInit, Component, Input, TrackByFunction } from "@angular/core";
import { BroadcastService } from "@bbwt/modules/broadcasting";
import { Subject, takeUntil } from "rxjs";
import { CACHE_COMPLETE, CACHE_COMPONENT_READY, CACHE_DATAGROUP_START, CACHE_URL_END, CACHE_URL_START } from "./cache-event-names";
import { CachingUrl, DatagroupStart, UrlEnd, UrlStart } from "./cache-types";

@Component({
    selector: "app-pwa-cache",
    template: `
        <p-sidebar
            [(visible)]="cacheInProgress && visible"
            appendTo="body"
            position="top"
            [dismissible]="cacheComplete"
            [showCloseIcon]="false"
            [modal]="false"
            [baseZIndex]="1000"
            styleClass="m-app-pwa-cache">
            <div class="app-cache-container">
                <ng-container *ngIf="!cacheComplete">
                    <h1 class="cache-header">We are caching some data for OFFLINE use—this may take a while</h1>
                    <h2 class="cache-datagroup">{{ dataGroupHeader }}</h2>
                    <div *ngFor="let cachingUrl of cachingUrls; trackBy: trackCachingUrl" class="caching-url">
                        <p>{{ cachingUrl.url }}</p>
                        <i *ngIf="cachingUrl.loading" class="pi pi-spin pi-spinner"></i>
                        <i *ngIf="!cachingUrl.loading && cachingUrl.type === '✔️'" class="pi pi-check"></i>
                        <i *ngIf="!cachingUrl.loading && cachingUrl.type === '❌'" class="pi pi-times text-danger"></i>
                    </div>
                </ng-container>
                <ng-container *ngIf="cacheComplete">
                    <h1 class="cache-header">We have finished caching data for OFFLINE use</h1>
                    <button
                        class="p-button-primary"
                        type="button"
                        pButton
                        label="Close"
                        icon="pi pi-times"
                        (click)="cacheInProgress = false"></button>
                </ng-container>
            </div>
        </p-sidebar>
    `,
    styleUrls: ["./app-pwa-cache.component.scss"]
})
export class AppPwaCacheComponent implements AfterViewInit {
    private destroyed$ = new Subject<void>();

    cacheInProgress = false;
    cacheComplete = false;
    dataGroupHeader: string;
    cachingUrls: CachingUrl[] = [];

    @Input() visible = false;

    constructor(private broadcastService: BroadcastService) {
        this.setupListeners();
    }

    ngAfterViewInit(): void {
        this.broadcastService.broadcast(CACHE_COMPONENT_READY);
    }

    trackCachingUrl: TrackByFunction<CachingUrl> = (_, loadingUrl: CachingUrl) => loadingUrl.name;

    private setupListeners() {
        this.listen<DatagroupStart>(CACHE_DATAGROUP_START, ({ header }) => {
            this.dataGroupHeader = header;
            this.cachingUrls = [];
            this.cacheInProgress = true;
        });

        this.listen<UrlStart>(CACHE_URL_START, urlStart => (this.cachingUrls = [...this.cachingUrls, { ...urlStart, loading: true }]));

        this.listen<UrlEnd>(CACHE_URL_END, ({ name, type, errorMessage }) => {
            const urlStart = this.cachingUrls.find(u => u.name === name);
            this.cachingUrls = [...this.cachingUrls.filter(u => u !== urlStart), { ...urlStart, loading: false, type, errorMessage }];
            if (this.cachingUrls.length > 5) {
                this.cachingUrls = this.cachingUrls.slice(-5);
            }
        });

        this.listen(CACHE_COMPLETE, _ => {
            this.cacheComplete = true;
            this.dataGroupHeader = null;
            this.cachingUrls = null;
            this.stopListening();
        });
    }

    private listen<T>(key: string, handler: (obj: T) => void) {
        this.broadcastService.on<T>(key).pipe(takeUntil(this.destroyed$)).subscribe(handler);
    }

    private stopListening() {
        this.destroyed$.next();
        this.destroyed$.complete();
    }
}
